import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Select, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import {
  EmailField, FieldDefaultsProvider, GradeField, PhoneField, TextField, type FieldDefaults,
} from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';
import { PlusOutlined } from '@ant-design/icons';
import type {
  CreateInquiryRequest, InquiryRow, InquirySource, InquiryStatus,
} from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Inquiries pipeline.
 * Real inquiries table with source + status + convert-to-application shortcut.
 * Kanban view arrives with the full Admissions module; today it's a filter list.
 */
const SOURCE_LABELS: Record<InquirySource, string> = {
  walk_in: 'Walk-in', call: 'Call', web: 'Web form', referral: 'Referral', other: 'Other',
};
const STATUS_TONE: Record<InquiryStatus, string> = {
  new: 'processing', contacted: 'warning', qualified: 'success', rejected: 'default', converted: 'purple',
};

export function InquiriesPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');

  const list = useQuery({
    queryKey: ['inquiries', institution],
    queryFn: () => productApi.listInquiries(institution),
    enabled: !!institution,
  });

  const update = useMutation({
    mutationFn: (v: { id: string; status?: InquiryStatus; notes?: string }) =>
      productApi.updateInquiry(institution, v.id, { status: v.status, notes: v.notes }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['inquiries', institution] }); },
  });

  const rows = (list.data?.rows ?? []).filter((r) => statusFilter === 'all' || r.status === statusFilter);
  const by = list.data?.by_status ?? { new: 0, contacted: 0, qualified: 0, rejected: 0, converted: 0 };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Inquiries</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Top-of-funnel enquiries. Move to <b>qualified</b> then convert to an application.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New inquiry</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="New" value={by.new} valueStyle={{ color: '#2f6ab0' }} /></Card>
        <Card size="small"><Statistic title="Contacted" value={by.contacted} valueStyle={{ color: '#a35800' }} /></Card>
        <Card size="small"><Statistic title="Qualified" value={by.qualified} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Converted" value={by.converted} valueStyle={{ color: '#553a9c' }} /></Card>
        <Card size="small"><Statistic title="Rejected" value={by.rejected} /></Card>
      </div>

      <Select
        size="small"
        value={statusFilter}
        onChange={setStatusFilter}
        style={{ width: 200 }}
        options={[
          { value: 'all', label: 'All' },
          { value: 'new', label: 'New' },
          { value: 'contacted', label: 'Contacted' },
          { value: 'qualified', label: 'Qualified' },
          { value: 'converted', label: 'Converted' },
          { value: 'rejected', label: 'Rejected' },
        ]}
      />

      <Card size="small" title={`${rows.length} inquiries`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No inquiries. <a onClick={() => setAddOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<InquiryRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Parent', dataIndex: 'parent_name', key: 'p', render: (v: string) => <b>{v}</b> },
              { title: 'Phone', dataIndex: 'phone', key: 'ph', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Child', dataIndex: 'child_name', key: 'c', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Grade', dataIndex: 'grade_interest', key: 'g', width: 80, render: (v: string | null) => v ?? '—' },
              { title: 'Source', dataIndex: 'source', key: 's', width: 110, render: (v: InquirySource) => <Tag>{SOURCE_LABELS[v]}</Tag> },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'st',
                width: 140,
                render: (v: InquiryStatus, r) => (
                  <Select
                    size="small"
                    value={v}
                    style={{ width: 130 }}
                    onChange={(next) => update.mutate({ id: r.id, status: next })}
                    options={[
                      { value: 'new', label: 'New' },
                      { value: 'contacted', label: 'Contacted' },
                      { value: 'qualified', label: 'Qualified' },
                      { value: 'rejected', label: 'Rejected' },
                      { value: 'converted', label: 'Converted', disabled: true },
                    ]}
                  />
                ),
              },
              {
                key: 'action',
                width: 140,
                align: 'right',
                render: (_, r) => (
                  r.status === 'qualified' ? (
                    <Button size="small" type="primary" onClick={() => navigate(`/i/${institution}/admin/admissions/applications?inquiry=${r.id}&name=${encodeURIComponent(r.child_name ?? '')}&parent=${encodeURIComponent(r.parent_name)}&phone=${encodeURIComponent(r.phone)}&grade=${encodeURIComponent(r.grade_interest ?? '')}`)}>
                      Convert
                    </Button>
                  ) : null
                ),
              },
            ]}
          />
        )}
      </Card>

      <AddInquiryModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['inquiries', institution] });
          void message.success('Inquiry added');
        }}
      />
    </Space>
  );
}

function AddInquiryModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const structure = useQuery({
    queryKey: ['structure', institution],
    queryFn: () => productApi.getStructure(institution),
    enabled: !!institution && open,
  });
  const grades = structure.data?.grades ?? [];
  const fieldDefaults = useMemo<FieldDefaults>(
    () => ({ grade: { options: grades.map((g) => ({ value: g.name, label: g.name })) } }),
    [grades],
  );
  return (
    <FormModal<CreateInquiryRequest>
      open={open} onClose={onClose}
      title="New inquiry" submitText="Add"
      initial={{ source: 'walk_in' }}
      mutation={(v) => productApi.createInquiry(institution, {
        ...v,
        email: v.email || undefined,
        child_name: v.child_name || undefined,
        grade_interest: v.grade_interest || undefined,
      })}
      onSuccess={onCreated}
    >
      <FieldDefaultsProvider value={fieldDefaults}>
        <FormSection cols={2}>
          <Field name="parent_name" label="Parent name" required>
            <TextField placeholder="Sunita Kulkarni" autoFocus />
          </Field>
          <Field name="phone" label="Phone" required rules={[V.phone()]}>
            <PhoneField />
          </Field>
          <Field name="email" label="Email" optional rules={[V.email()]}>
            <EmailField placeholder="parent@example.in" />
          </Field>
          <Field name="source" label="Source" required>
            <Select options={(Object.keys(SOURCE_LABELS) as InquirySource[]).map((s) => ({ value: s, label: SOURCE_LABELS[s] }))} />
          </Field>
          <Field name="child_name" label="Child name">
            <TextField placeholder="Aarav" />
          </Field>
          <Field name="grade_interest" label="Grade interest">
            <GradeField />
          </Field>
        </FormSection>
        <Field name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="What brought them in, follow-ups agreed…" />
        </Field>
      </FieldDefaultsProvider>
    </FormModal>
  );
}
